import React, { ReactNode } from 'react';

import { Box, BoxProps, HStack, Stack } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { customTheme } from 'theme';

import { PageHeaderWithIcon } from 'components/DataDisplay';
import { OutlinedIconButton } from 'components/DataEntry/Button/OutlinedIconButton';
import { IconType, PrinterIcon, ThirdPartyIcon } from 'components/Icons';
import { CDashboard } from 'components/Visualization';
import { IDashboardLayout } from 'components/Visualization/CDashboard/types';
import { selectApp } from 'containers/App/selectors';
import { useSharedStateContext } from 'containers/SharedState/context/context';

interface Props {
  content?: ReactNode;
  label: string;
  icon?: ReactNode;
  rightHeader?: ReactNode;
  height?: string | number;
  titleBgColor?: string;
  iconBgColor?: string;
  titleFontColor?: string;
  dashboardWidgets?: IDashboardLayout;
  redirectLink?: {
    link: string;
    label?: string;
    localStorageKey?: string;
    localStorageValue?: string;
  };
  iconType?: IconType;
  iconStyles?: BoxProps;
}

export const DashboardOverviewWrapperCard: React.FC<Props> = props => {
  const {
    label,
    content,
    icon,
    height,
    iconBgColor = 'primary',
    titleFontColor = 'black',
    dashboardWidgets,
    redirectLink,
    iconType,
    iconStyles,
    rightHeader,
  } = props;
  const navigate = useNavigate();
  const { contentRef } = useSharedStateContext();
  const { user } = useSelector(selectApp);

  const handlePrint = useReactToPrint({
    contentRef,
    documentTitle:
      user.info.org_id +
      '-' +
      label +
      '-' +
      dayjs().utc().format('DD-MMMM-YYYY hh-mmA'),
    pageStyle: `
      @page {
        layout: landscape;
        margin: 0;
        size: 17in 11in;
      }
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    `,
  });

  return (
    <Stack overflowY="auto">
      <HStack
        color="primary"
        bg="white"
        py={2}
        px={3}
        borderRadius={6}
        border="1px solid"
        borderColor={customTheme.colors.gray['100']}
        justify="space-between"
      >
        <PageHeaderWithIcon
          label={label}
          icon={icon}
          border
          iconSize="md"
          borderColor={customTheme.colors.gray['250']}
          iconBgColor={iconBgColor}
          iconStyles={{ borderRadius: 12, ...iconStyles }}
          fontColor={titleFontColor}
          iconType={iconType}
          iconColor="white"
        />
        {rightHeader}
        {redirectLink && (
          <HStack spacing={3}>
            <OutlinedIconButton
              leftIcon={<PrinterIcon />}
              onClick={() => handlePrint()}
            >
              PDF
            </OutlinedIconButton>

            <OutlinedIconButton
              leftIcon={<ThirdPartyIcon />}
              onClick={() => {
                if (!!redirectLink?.localStorageKey) {
                  localStorage.setItem(
                    redirectLink?.localStorageKey,
                    redirectLink?.localStorageValue ?? '',
                  );
                }
                navigate(redirectLink?.link);
              }}
            >
              {redirectLink?.label ?? 'Findings'}
            </OutlinedIconButton>
          </HStack>
        )}
      </HStack>
      <Box flex={1} h="full" py={2} px={0}>
        {dashboardWidgets ? (
          <CDashboard dashboardWidgets={dashboardWidgets} />
        ) : (
          content
        )}
      </Box>
    </Stack>
  );
};
